import { Injectable } from '@angular/core';
import Swal from 'sweetalert2';

export type AlertTone = 'info' | 'warning' | 'danger' | 'success';

export type AlertIllustration =
  | 'archive'
  | 'cancel'
  | 'card'
  | 'chat'
  | 'close-order'
  | 'cost'
  | 'error'
  | 'info'
  | 'lock'
  | 'message'
  | 'notification'
  | 'payment'
  | 'role'
  | 'success'
  | 'trash'
  | 'wallet'
  | 'warning';

export interface WarningOptions {
  confirmButtonText?: string;
  cancelButtonText?: string;
  tone?: AlertTone;
  illustration?: AlertIllustration;
  notice?: string;
  presentation?: 'chat-bulk-send-preview';
}

export interface FeedbackOptions {
  confirmButtonText?: string;
  tone?: AlertTone;
  illustration?: AlertIllustration;
  notice?: string;
}

interface IllustrationPalette {
  color: string;
  colorEnd: string;
  soft: string;
  softEnd: string;
}

@Injectable({
  providedIn: 'root',
})
export class SwalService {
  success(message: string, text?: string, options?: FeedbackOptions): void {
    void this.openFeedback(message, text, {
      ...options,
      tone: options?.tone || 'success',
      illustration: options?.illustration || 'success',
    });
  }

  error(
    message: string = 'مشکلی پیش آمد',
    text: string = 'لطفاً دوباره تلاش کنید',
    options?: FeedbackOptions,
  ): void {
    void this.openFeedback(message, text, {
      ...options,
      tone: options?.tone || 'danger',
      illustration: options?.illustration || 'error',
    });
  }

  info(message: string, text?: string, options?: FeedbackOptions): void {
    void this.openFeedback(message, text, {
      ...options,
      tone: options?.tone || 'info',
      illustration: options?.illustration || 'info',
    });
  }

  warning(
    message: string,
    text?: string,
    options?: WarningOptions,
  ): Promise<boolean> {
    const isLegacyChatPreview = options?.presentation === 'chat-bulk-send-preview';

    return this.openConfirmation(message, text, {
      ...options,
      tone: options?.tone || (isLegacyChatPreview ? 'info' : 'warning'),
      illustration:
        options?.illustration || (isLegacyChatPreview ? 'message' : 'warning'),
    });
  }

  confirmDelete(
    message: string = 'آیا از حذف این مورد اطمینان دارید؟',
    text: string = 'این عملیات قابل بازگشت نیست.',
  ): Promise<boolean> {
    return this.openConfirmation(message, text, {
      tone: 'danger',
      illustration: 'trash',
      confirmButtonText: 'حذف',
      cancelButtonText: 'انصراف',
      notice: 'پس از حذف، امکان بازیابی این مورد وجود ندارد.',
    });
  }

  private openFeedback(
    message: string,
    text: string | undefined,
    options: FeedbackOptions,
  ): Promise<boolean> {
    const tone = options.tone || 'info';
    const illustration = options.illustration || 'info';

    return Swal.fire({
      title: message,
      html: this.createContent(text, options.notice),
      icon: 'info',
      iconHtml: this.createIllustration(illustration, tone),
      confirmButtonText: options.confirmButtonText || 'متوجه شدم',
      showCancelButton: false,
      buttonsStyling: false,
      backdrop: 'rgba(15, 23, 42, 0.62)',
      customClass: this.createCustomClasses(tone, illustration, false),
    }).then((result) => result.isConfirmed);
  }

  private openConfirmation(
    message: string,
    text: string | undefined,
    options: WarningOptions,
  ): Promise<boolean> {
    const tone = options.tone || 'warning';
    const illustration = options.illustration || 'warning';

    return Swal.fire({
      title: message,
      html: this.createContent(text, options.notice),
      icon: 'info',
      iconHtml: this.createIllustration(illustration, tone),
      confirmButtonText: options.confirmButtonText || 'تأیید',
      cancelButtonText: options.cancelButtonText || 'انصراف',
      showCancelButton: true,
      buttonsStyling: false,
      focusCancel: true,
      backdrop: 'rgba(15, 23, 42, 0.62)',
      customClass: this.createCustomClasses(tone, illustration, true),
    }).then((result) => result.isConfirmed);
  }

  private createCustomClasses(
    tone: AlertTone,
    illustration: AlertIllustration,
    hasCancelButton: boolean,
  ) {
    return {
      container: 'app-confirmation-backdrop',
      popup: `app-confirmation app-confirmation--${tone}`,
      icon: `app-confirmation__hero-icon app-confirmation__hero-icon--${illustration}`,
      title: 'app-confirmation__title',
      htmlContainer: 'app-confirmation__content',
      actions: `app-confirmation__actions${
        hasCancelButton ? '' : ' app-confirmation__actions--single'
      }`,
      confirmButton: 'app-confirmation__confirm',
      cancelButton: 'app-confirmation__cancel',
    };
  }

  private createContent(text?: string, notice?: string): string | undefined {
    if (!text && !notice) return undefined;

    const description = text
      ? `<p class="app-confirmation__description">${this.escapeHtml(text)}</p>`
      : '';
    const noticeContent = notice
      ? `
        <div class="app-confirmation__notice" role="note">
          <span class="app-confirmation__notice-icon" aria-hidden="true">i</span>
          <span>${this.escapeHtml(notice)}</span>
        </div>
      `
      : '';

    return `${description}${noticeContent}`;
  }

  private createIllustration(
    illustration: AlertIllustration,
    tone: AlertTone,
  ): string {
    if (illustration === 'message') {
      return this.createMessageIllustration();
    }

    const palette = this.getPalette(tone);
    const icon = this.getIconMarkup(illustration);

    return `
      <svg class="app-confirmation__illustration" viewBox="0 0 160 120" aria-hidden="true" focusable="false">
        <defs>
          <linearGradient id="confirmation-soft-${tone}" x1="25" y1="14" x2="137" y2="108" gradientUnits="userSpaceOnUse">
            <stop stop-color="${palette.soft}" />
            <stop offset="1" stop-color="${palette.softEnd}" />
          </linearGradient>
          <linearGradient id="confirmation-main-${tone}" x1="50" y1="36" x2="111" y2="94" gradientUnits="userSpaceOnUse">
            <stop stop-color="${palette.color}" />
            <stop offset="1" stop-color="${palette.colorEnd}" />
          </linearGradient>
          <filter id="confirmation-shadow-${tone}" x="35" y="22" width="90" height="90" filterUnits="userSpaceOnUse">
            <feDropShadow dx="0" dy="8" stdDeviation="8" flood-color="${palette.colorEnd}" flood-opacity="0.28" />
          </filter>
        </defs>
        <path class="app-confirmation__blob" d="M29 84c-13-11-13-31-3-44 9-12 25-16 39-12 10-15 31-20 48-10 14 8 21 25 16 40 13 9 17 27 8 40-9 14-28 18-43 10-13 10-32 11-45 1-8-6-13-15-14-24l-6-1Z" fill="url(#confirmation-soft-${tone})" />
        <circle class="app-confirmation__spark app-confirmation__spark--one" cx="27" cy="35" r="5" fill="${palette.color}" opacity="0.42" />
        <circle class="app-confirmation__spark app-confirmation__spark--two" cx="136" cy="33" r="4" fill="${palette.colorEnd}" opacity="0.38" />
        <path class="app-confirmation__spark app-confirmation__spark--three" d="m136 84 3 7 7 3-7 3-3 7-3-7-7-3 7-3 3-7Z" fill="${palette.color}" opacity="0.45" />
        <g class="app-confirmation__symbol" filter="url(#confirmation-shadow-${tone})">
          <circle cx="80" cy="64" r="35" fill="url(#confirmation-main-${tone})" />
          ${icon}
        </g>
      </svg>
    `;
  }

  private createMessageIllustration(): string {
    return `
      <svg class="app-confirmation__illustration" viewBox="0 0 160 120" aria-hidden="true" focusable="false">
        <defs>
          <linearGradient id="chat-confirmation-cloud" x1="18" y1="18" x2="137" y2="105" gradientUnits="userSpaceOnUse">
            <stop stop-color="#d7f2ff" />
            <stop offset="0.5" stop-color="#d8c9ff" />
            <stop offset="1" stop-color="#b9d7ff" />
          </linearGradient>
          <linearGradient id="chat-confirmation-plane" x1="53" y1="91" x2="120" y2="31" gradientUnits="userSpaceOnUse">
            <stop stop-color="#6947d9" />
            <stop offset="0.55" stop-color="#7067ee" />
            <stop offset="1" stop-color="#2aabee" />
          </linearGradient>
          <filter id="chat-confirmation-shadow" x="34" y="16" width="106" height="102" filterUnits="userSpaceOnUse">
            <feDropShadow dx="0" dy="7" stdDeviation="7" flood-color="#6c63df" flood-opacity="0.3" />
          </filter>
        </defs>
        <path class="app-confirmation__blob" d="M38 89c-15 0-26-10-26-23 0-12 10-22 23-23 5-17 19-29 37-29 15 0 29 8 35 21 3-1 7-2 11-2 17 0 30 12 30 28s-13 28-30 28H38Z" fill="url(#chat-confirmation-cloud)" />
        <g class="app-confirmation__symbol" filter="url(#chat-confirmation-shadow)">
          <path d="m41 60 79-31c6-2 10 2 8 8l-23 67c-2 6-7 7-11 3L78 93l-10 9c-3 3-6 2-6-3l2-23-22-9c-6-2-6-5-1-7Z" fill="url(#chat-confirmation-plane)" />
          <path d="m64 76 47-32-33 49-10 9c-3 3-6 2-6-3l2-23Z" fill="#4936ad" opacity="0.78" />
          <path d="m64 76 47-32-33 49Z" fill="#c5d6ff" opacity="0.76" />
        </g>
      </svg>
    `;
  }

  private getIconMarkup(illustration: AlertIllustration): string {
    const paths: Partial<Record<AlertIllustration, string>> = {
      archive: '<path d="M55 52h50v43H55V52Zm-5-13h60v15H50V39Zm20 28h20" fill="none" stroke="#fff" stroke-width="7" stroke-linecap="round" stroke-linejoin="round" />',
      cancel: '<circle cx="80" cy="64" r="19" fill="none" stroke="#fff" stroke-width="7" /><path d="m66 78 28-28" stroke="#fff" stroke-width="7" stroke-linecap="round" />',
      card: '<rect x="54" y="47" width="52" height="35" rx="6" fill="none" stroke="#fff" stroke-width="6" /><path d="M56 58h48M65 71h13" stroke="#fff" stroke-width="6" stroke-linecap="round" />',
      chat: '<path d="M55 48h50v34H80L64 94V82h-9V48Z" fill="none" stroke="#fff" stroke-width="6" stroke-linejoin="round" /><path d="M68 64h24" stroke="#fff" stroke-width="6" stroke-linecap="round" />',
      'close-order': '<rect x="57" y="42" width="46" height="48" rx="6" fill="none" stroke="#fff" stroke-width="6" /><path d="M70 39h20v10H70V39Zm-2 26 8 8 17-18" fill="none" stroke="#fff" stroke-width="6" stroke-linecap="round" stroke-linejoin="round" />',
      cost: '<ellipse cx="73" cy="72" rx="20" ry="8" fill="none" stroke="#fff" stroke-width="6" /><path d="M53 60c0 5 9 9 20 9s20-4 20-9-9-9-20-9-20 4-20 9v24c0 5 9 9 20 9 6 0 12-1 16-4" fill="none" stroke="#fff" stroke-width="6" /><circle cx="96" cy="78" r="12" fill="none" stroke="#fff" stroke-width="6" />',
      error: '<circle cx="80" cy="64" r="21" fill="none" stroke="#fff" stroke-width="7" /><path d="m69 53 22 22m0-22L69 75" stroke="#fff" stroke-width="7" stroke-linecap="round" />',
      info: '<circle cx="80" cy="64" r="22" fill="none" stroke="#fff" stroke-width="7" /><path d="M80 61v17m0-29v1" stroke="#fff" stroke-width="7" stroke-linecap="round" />',
      lock: '<rect x="59" y="59" width="42" height="31" rx="6" fill="none" stroke="#fff" stroke-width="6" /><path d="M68 59V49a12 12 0 0 1 24 0v10M80 70v9" fill="none" stroke="#fff" stroke-width="6" stroke-linecap="round" />',
      notification: '<path d="M61 77h38l-5-8V57a14 14 0 0 0-28 0v12l-5 8Zm13 9h12" fill="none" stroke="#fff" stroke-width="6" stroke-linecap="round" stroke-linejoin="round" />',
      payment: '<rect x="53" y="46" width="54" height="38" rx="7" fill="none" stroke="#fff" stroke-width="6" /><path d="M54 58h52M66 72h12" stroke="#fff" stroke-width="6" stroke-linecap="round" /><circle cx="96" cy="73" r="5" fill="#fff" />',
      role: '<circle cx="74" cy="55" r="10" fill="none" stroke="#fff" stroke-width="6" /><path d="M55 82c2-12 9-18 19-18 7 0 12 3 15 8m4-13 12 5v10c0 8-5 13-12 16-7-3-12-8-12-16V64l12-5Z" fill="none" stroke="#fff" stroke-width="6" stroke-linecap="round" stroke-linejoin="round" />',
      success: '<circle cx="80" cy="64" r="22" fill="none" stroke="#fff" stroke-width="7" /><path d="m67 64 9 9 18-20" fill="none" stroke="#fff" stroke-width="7" stroke-linecap="round" stroke-linejoin="round" />',
      trash: '<path d="M61 52h38M72 45h16m-23 7 3 39h24l3-39M76 63v17m9-17v17" fill="none" stroke="#fff" stroke-width="6" stroke-linecap="round" stroke-linejoin="round" />',
      wallet: '<path d="M54 51h48a6 6 0 0 1 6 6v31H60a6 6 0 0 1-6-6V51Zm0 9h48m-8 9h14v12H94a6 6 0 0 1 0-12Z" fill="none" stroke="#fff" stroke-width="6" stroke-linejoin="round" />',
      warning: '<path d="m80 42 27 47H53l27-47Z" fill="none" stroke="#fff" stroke-width="7" stroke-linejoin="round" /><path d="M80 59v13m0 9v1" stroke="#fff" stroke-width="7" stroke-linecap="round" />',
    };

    return paths[illustration] || paths.info || '';
  }

  private getPalette(tone: AlertTone): IllustrationPalette {
    const palettes: Record<AlertTone, IllustrationPalette> = {
      info: {
        color: '#2aabee',
        colorEnd: '#6757db',
        soft: '#d8f2ff',
        softEnd: '#ddd4ff',
      },
      warning: {
        color: '#f6a723',
        colorEnd: '#e67418',
        soft: '#fff0c7',
        softEnd: '#ffd8b5',
      },
      danger: {
        color: '#f05a67',
        colorEnd: '#c9364d',
        soft: '#ffe0e4',
        softEnd: '#ffc8d0',
      },
      success: {
        color: '#36bf87',
        colorEnd: '#159b70',
        soft: '#d8f8e9',
        softEnd: '#bdeedb',
      },
    };

    return palettes[tone];
  }

  private escapeHtml(value: string): string {
    return value.replace(/[&<>'"]/g, (character) => {
      const entities: Record<string, string> = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        "'": '&#039;',
        '"': '&quot;',
      };

      return entities[character];
    });
  }
}
